import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, RefreshCw, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function to extract content between triple backticks
function extractCodeFromBackticks(content) {
  if (!content) return content;
  
  // Match content between triple backticks (```)
  const codeBlockRegex = /```[\s\S]*?```/g;
  const matches = content.match(codeBlockRegex);
  
  if (matches && matches.length > 0) {
    // Extract content from all code blocks and join them
    return matches
      .map(match => {
        // Remove the triple backticks from start and end
        return match.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
      })
      .join('\n\n');
  }
  
  // If no triple backticks found, return original content
  return content;
}

export default function CodeEditor() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [codeGenerations, setCodeGenerations] = useState([]);
  const [activeCode, setActiveCode] = useState(null);
  const [editedCode, setEditedCode] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [ticketId]);

  const fetchData = async () => {
    try {
      // ticketId prop is now ticket_number
      const [ticketRes, codeRes] = await Promise.all([
        axios.get(`${API}/tickets/${ticketId}`),
        axios.get(`${API}/code/${ticketId}`)
      ]);

      setTicket(ticketRes.data);
      setCodeGenerations(codeRes.data);
      
      const active = codeRes.data.find(c => c.is_active) || codeRes.data[0];
      if (active) {
        setActiveCode(active);
        setEditedCode(extractCodeFromBackticks(active.code));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCode = async () => {
    if (!activeCode) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/code/${activeCode.id}`, {
        code: editedCode,
        human_feedback: feedback || null
      });
      await fetchData();
      setFeedback('');
    } catch (error) {
      console.error('Error saving code:', error);
    } finally {
      setSaving(false);
    }
  };

  const regenerateCode = async () => {
    if (!activeCode || !feedback.trim()) return;

    setRegenerating(true);
    try {
      await axios.post(`${API}/code/${activeCode.id}/regenerate`, null, {
        params: { feedback }
      });
      await fetchData();
      setFeedback('');
    } catch (error) {
      console.error('Error regenerating code:', error);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  if (!ticket || !activeCode) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-lg mb-4">No code generated yet for this ticket</div>
          <Button onClick={() => navigate(`/tickets/${ticketId}`)}>Go to Ticket</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full flex flex-col" data-testid="code-editor-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vibe-studio')} data-testid="back-to-studio-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono">{ticket.ticket_number}</Badge>
              <Badge variant="outline">{activeCode.language}</Badge>
              <Badge>v{activeCode.version}</Badge>
            </div>
            <h1 className="text-2xl font-bold">{ticket.title}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={saveCode} disabled={saving || editedCode === extractCodeFromBackticks(activeCode.code)} data-testid="save-code-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 flex-1">
        <div className="col-span-2 space-y-4">
          <Card className="flex-1">
            <CardHeader>
              <CardTitle>Code Editor</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={editedCode}
                onChange={(e) => setEditedCode(e.target.value)}
                className="font-mono min-h-[600px] bg-slate-900 text-slate-50"
                data-testid="code-editor-textarea"
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Collaboration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="feedback">Feedback for AI</Label>
                <Textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Provide feedback to improve the code..."
                  rows={6}
                  data-testid="feedback-textarea"
                />
              </div>
              <Button 
                onClick={regenerateCode} 
                disabled={regenerating || !feedback.trim()} 
                className="w-full"
                data-testid="regenerate-code-btn"
              >
                {regenerating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Regenerate with AI
              </Button>
              <Alert>
                <AlertDescription className="text-xs">
                  The AI agent will analyze your feedback and generate an improved version of the code.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Version History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {codeGenerations.map((code) => (
                  <div 
                    key={code.id} 
                    className={`p-3 border rounded-lg cursor-pointer hover:bg-accent ${code.id === activeCode.id ? 'bg-accent' : ''}`}
                    onClick={() => {
                      setActiveCode(code);
                      setEditedCode(extractCodeFromBackticks(code.code));
                    }}
                    data-testid={`version-${code.version}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant={code.is_active ? 'default' : 'outline'}>v{code.version}</Badge>
                      <div className="text-xs text-muted-foreground">
                        {new Date(code.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    {code.human_feedback && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {code.human_feedback.slice(0, 60)}{code.human_feedback.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Code Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <div className="text-muted-foreground">Language</div>
                <div className="font-medium">{activeCode.language}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Description</div>
                <div className="font-medium">{activeCode.description}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Created</div>
                <div className="font-medium">{new Date(activeCode.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Last Updated</div>
                <div className="font-medium">{new Date(activeCode.updated_at).toLocaleString()}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
